import { Component, Element, Host, EventEmitter, h, Prop, Listen, Method, Event, AttachInternals } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { IconName } from '../icon/icons';
import { setFormValue, setValidityMissingValue } from '../../utils/formAssociated';
import { CheckboxEventDetail } from '../../utils';

@Component({
  tag: `anchor-checkbox`,
  styleUrl: 'checkbox.scss',
  shadow: {
    delegatesFocus: true
  },
  formAssociated: true,
})
export class Checkbox {
  @Element() host: HTMLInputElement;

  @AttachInternals() internals: ElementInternals;

  formResetCallback() {
    this.internals.setValidity({});
    setFormValue(this.internals, null);
  }

  formStateRestoreCallback(state: string) {
    setFormValue(this.internals, state);
    setValidityMissingValue(this.internals, state, this.validityMissingValueText)
  }

  /**
   *  Give this instance of checkbox a custom ID, or keep the default.
   */
  @Prop({}) uuid: string = uuidv4();

  /**
   * This is the name for the entire checkbox group
   */
  @Prop() name: string;

  /**
   * This is the string that is returned as a value, when the form is submitted
   */
  @Prop() value: string = 'Checkbox';

  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /**
   * Whether or not the checkbox is indeterminate.
   * The indeterminate state is purely visual and does not change the checked value of the checkbox.
   */
  @Prop({ reflect: true, mutable: true }) indeterminate: boolean = false;

  /**
   * Disable the checkbox
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Mark the field as required
   */
  @Prop() required: boolean = false;

  @Prop() icon: IconName = this.indeterminate ? 'minus' : 'check';

  /**
   * Text to be displayed if when value is missing an native form validation is used
   */
  @Prop() validityMissingValueText?: string;

  @Event() toggled: EventEmitter<CheckboxEventDetail>;

  @Method()
  async toggleCheckbox(e = undefined) {
    if (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    if (!this.disabled) {
      this.checked = !this.checked;
      setFormValue(this.internals, this.checked ? this.value: null)
      if (this.required)
        setValidityMissingValue(this.internals, this.checked ? this.value: null, this.validityMissingValueText);
      this.internals.ariaChecked = String(this.checked);
      this.toggled.emit({ checked: this.checked, value: this.value });
    }
  }

  @Listen('keydown')
  handleKeyDown(e: KeyboardEvent) {
    switch (e.code) {
      case 'Enter':
      case 'Space':
        this.toggleCheckbox(e);
        break;
      default:
        break;
    }
  }

  @Listen('click')
  handleClick(e: PointerEvent) {
    this.toggleCheckbox(e);
  }

  componentWillRender() {
    this.host.indeterminate = this.indeterminate;
    setFormValue(this.internals, this.checked ? this.value: null)
    if (this.required)
        setValidityMissingValue(this.internals, this.checked ? this.value: null, this.validityMissingValueText);
    this.internals.ariaChecked = String(this.checked);
  }

  render() {
    return (
      <Host>
        <anchor-label
          class={`
            anchor-c-checkbox 
            ${this.disabled ? 'anchor-c-checkbox--disabled' : ''} 
          `}
          for={this.uuid}
          tabindex="0"
        >
          <input
            id={this.uuid}
            name={this.name}
            type="checkbox"
            class={`anchor-c-checkbox--input ${this.indeterminate ? 'indeterminate' : ''}`}
            checked={this.checked}
            disabled={this.disabled}
            tabindex="-1"
            value={this.value}
          />
          {(this.checked || this.indeterminate) && <anchor-icon name={this.icon} size="small" class="icon" color="var(--s-color-icon-on-dark)" stroke={2} />}
          <span class="text">
            <slot />
            <slot name="help-text" />
          </span>
        </anchor-label>
      </Host>
    );
  }
}
